import { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";

import useUserStore from "data/User.store";
import useRbacStore from "data/Rbac.store";
import LoaderDefault from "components/common/Loader.default/Loader.default";
import AccessDenied from "components/common/AccessDenied/AccessDenied";

const AppLayout = lazy(() => import("./AppLayout"));
const Dashboard = lazy(() => import("containers/Dashboard/Dashboard"));
const CreateBooking = lazy(
  () => import("containers/CreateBooking/CreateBooking")
);
const RatesBase = lazy(() => import("containers/Rates.base/Rates.base"));
const RatesForApproval = lazy(
  () => import("containers/RatesForApproval/RatesForApproval")
);
const AgentReviewSearch = lazy(
  () => import("containers/Agent.review.search/Agent.review.search")
);
const AgentReviewForm = lazy(
  () => import("containers/Agent.review.form/Agent.review.form")
);
const ShipmentTracking = lazy(
  () => import("containers/ShipmentTracking/ShipmentTracking")
);
const ShipmentQueryResults = lazy(
  () => import("containers/ShipmentQueryResults/ShipmentQueryResults")
);
const ShippingTimelines = lazy(
  () => import("containers/Shipping.timelines/Shipping.Timelines")
);
const FinancialDashboard = lazy(() => import("containers/FinancialDashboard/FinancialDashboard"));
const FinancialChargeDetails = lazy(
  () => import("containers/FinancialDashboard/ChargeDetails")
);
const CustomerProfiles = lazy(
  () => import("containers/CustomerProfiles/CustomerProfiles")
);
const ArchivedCustomerProfiles = lazy(
  () => import("containers/ArchivedCustomerProfiles/ArchivedCustomerProfiles")
);
const CustomReports = lazy(() => import("containers/CustomReports"));
const ExportInvoicingReports = lazy(
  () => import("containers/CustomReports/ExportInvoicingReports")
);
const RateMatrix = lazy(() => import("containers/RateMatrix/RateMatrix"));
const MarketUpdates = lazy(
  () => import("containers/MarketUpdates/MarketUpdates")
);
const ImportReports = lazy(
  () => import("containers/ImportReports/ImportReports")
);
const TradeBulletins = lazy(
  () => import("containers/TradeBulletins/TradeBulletins")
);

// {/* <Route path="rates" element={<ProtectedRoutes/>}> */}
// this works so far but it is hard coded in the ProtectedRoutes
// so try to figure out how to make it not hard-coded
// /rates/approvals
interface IProtectedRouteProps {
  isAllowed: boolean;
  children?: any;
}

const ProtectedRoute = ({
  isAllowed,
  children,
}: IProtectedRouteProps) => {
  if (!isAllowed) {
    return <AccessDenied />;
  }

  return children ? children : <Outlet />;
};

const AUTH_SERVICE_URL =
  process.env.REACT_APP_AUTH_SERVICE_URL || "";
const APP_URL = process.env.REACT_APP_APP_URL || (typeof window !== "undefined" ? window.location.origin : "");

function RedirectToAuthService() {
  useEffect(() => {
    if (AUTH_SERVICE_URL) {
      const redirectUrl = APP_URL || (typeof window !== "undefined" ? window.location.origin : "");
      window.location.href = `${AUTH_SERVICE_URL}?redirect_url=${encodeURIComponent(redirectUrl)}`;
    }
  }, []);
  return <LoaderDefault text="Redirecting to sign in..." />;
}

//should be controlling all data
function AppRouting() {
  const { user, checkUserExists, userExistsChecked } = useUserStore();
  const { hasPermission, loaded: rbacLoaded } = useRbacStore();

  useEffect(() => {
    if (!userExistsChecked) {
      checkUserExists();
    }
    //eslint-disable-next-line
  }, []);

  if (!userExistsChecked) {
    return <LoaderDefault text="Loading application..." />;
  }

  if (user && !rbacLoaded) {
    return <LoaderDefault text="Loading permissions..." />;
  }

  function permissionExists(permission: string) {
    return hasPermission(permission);
  }

  return (
    <Router>
      <Suspense fallback={<LoadingOverlay visible />}>
        <Routes>
          {!user ? (
            <Route path="*" element={<RedirectToAuthService />} />
          ) : (
            <>
              <Route path="/" element={<AppLayout />}>
                <Route index element={<Navigate replace to="dashboard" />} />
                <Route
                  path="dashboard"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.dashboard.read")}
                    >
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="rates"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.rates.approve")}
                    >
                      <RatesBase />
                    </ProtectedRoute>
                  }
                >
                  <Route path="approvals" element={<RatesForApproval />} />
                </Route>
                <Route
                  path="bookings"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.bookings.read")}
                    >
                      <div>Rates</div>
                    </ProtectedRoute>
                  }
                >
                  <Route path="approvals" element={<div>Bookings</div>} />
                </Route>
                <Route
                  path="create-booking"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.bookings.write")}
                    >
                      <CreateBooking />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="agent"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.agents.read")}
                    />
                  }
                >
                  <Route path="review" element={<AgentReviewSearch />}>
                    <Route path=":agentId" element={<AgentReviewForm />} />
                  </Route>
                </Route>
                <Route
                  path="shipment-tracking"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.shipments.read")}
                    >
                      <ShipmentTracking />
                    </ProtectedRoute>
                  }
                >
                  <Route path="shipments" element={<ShipmentQueryResults />}>
                    <Route
                      path=":shipmentNumber"
                      element={<ShippingTimelines />}
                    />
                  </Route>
                </Route>
                <Route
                  path="financial-dashboard"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.financials.read")}
                    >
                      <FinancialDashboard />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="chargedetails"
                    element={<FinancialChargeDetails />}
                  />
                </Route>
                <Route
                  path="customer-profiles"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.customers.read")}
                    >
                      <CustomerProfiles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="archived-customer-profiles"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.customers.read")}
                    >
                      <ArchivedCustomerProfiles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="custom-reports"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.reports.read")}
                    >
                      <CustomReports />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="export-invoicing-reports"
                    element={<ExportInvoicingReports />}
                  />
                </Route>
                <Route
                  path="rate-matrix"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.rates.read")}
                    >
                      <RateMatrix />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="market-updates"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.market_updates.read")}
                    >
                      <MarketUpdates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="import-reports"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.import_reports.read")}
                    >
                      <ImportReports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="trade-bulletins"
                  element={
                    <ProtectedRoute
                      isAllowed={permissionExists("agl_dashboard.trade_bulletins.read")}
                    >
                      <TradeBulletins />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate replace to="dashboard" />} />
            </>
          )}
        </Routes>
      </Suspense>
    </Router>
  );
}

function RequireAuth({ children, redirectTo }: any) {
  let isAuthenticated = localStorage.getItem("logged_in") === "true";
  return isAuthenticated ? children : <Navigate to={redirectTo} />;
}

export default AppRouting;
